import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { CookingPot, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CAT_LABELS = {
  'pequeno-almoco': 'Peq.-almoço',
  'almoco': 'Almoço',
  'jantar': 'Jantar',
  'lanche': 'Lanche',
  'ceia': 'Ceia',
};

const PT_LABELS = {
  gravida: 'Super Barriguinhas',
  introducao_alimentar: 'Super Bebés',
  pos_parto: 'Super Mamãs',
  pre_concecao: 'Super Pré Mamãs',
  geral: 'Geral',
};

export default function MinhasReceitasPage() {
  return (
    <ErrorBoundary>
      <MinhasReceitasInner />
    </ErrorBoundary>
  );
}

function MinhasReceitasInner() {
  const { user } = useAuth();
  const isClient = user?.role === 'cliente';
  const [data, setData] = useState({ recipes: [], program_phase: '', patient_type: '' });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/my-recipes-suggested`);
      const d = res?.data || {};
      setData({
        recipes: Array.isArray(d.recipes) ? d.recipes : [],
        program_phase: d.program_phase || '',
        patient_type: d.patient_type || '',
      });
    } catch {
      setData({ recipes: [], program_phase: '', patient_type: '' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const s = (search || '').toLowerCase().trim();
    const list = Array.isArray(data.recipes) ? data.recipes : [];
    if (!s) return list;
    return list.filter(r => (r?.name || '').toLowerCase().includes(s) || (r?.tags || []).some(t => (t || '').toLowerCase().includes(s)));
  }, [data, search]);

  const grouped = useMemo(() => {
    const out = {};
    (filtered || []).forEach(r => {
      const cat = r?.category || 'outros';
      if (!out[cat]) out[cat] = [];
      out[cat].push(r);
    });
    return out;
  }, [filtered]);

  if (loading) return <div className="fade-in text-center py-12 text-brand-muted">A carregar receitas…</div>;

  return (
    <div className="fade-in" data-testid="minhas-receitas-page">
      <div className="mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Receitas sugeridas</h2>
        <p className="text-xs text-brand-muted mt-1">
          {isClient ? 'Sugestões personalizadas para o teu programa' : 'Pré-visualização (vista da cliente)'}
          {isClient && data.patient_type ? ` · ${PT_LABELS[data.patient_type] || data.patient_type}` : ''}
        </p>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Pesquisar receita…"
          className="pl-8 rounded-xl border-brand-border text-sm"
          data-testid="my-recipes-search"
        />
      </div>

      {(!data.patient_type || data.patient_type === 'geral') && (data.recipes || []).length === 0 && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-6 text-center">
          <CookingPot size={28} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
          <p className="text-sm text-brand-muted">Ainda não há receitas atribuídas ao teu programa.</p>
          <p className="text-xs text-brand-placeholder mt-1">A tua nutricionista pode adicionar receitas específicas para ti.</p>
        </div>
      )}

      <div className="space-y-5">
        {Object.entries(grouped).map(([cat, recs]) => (
          <section key={cat}>
            <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-2">{CAT_LABELS[cat] || cat}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {(recs || []).map(r => (
                <button
                  key={r?.id || Math.random()}
                  onClick={() => setViewing(r)}
                  className="text-left bg-white border border-brand-border rounded-2xl p-3 hover:border-brand-green/40 transition-colors"
                  data-testid={`my-recipe-${r?.id || ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                      <CookingPot size={18} className="text-brand-green" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brand-text truncate">{r?.name || 'Sem título'}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(r?.tags || []).slice(0, 3).map((t, i) => <span key={`${t}-${i}`} className="text-[10px] bg-brand-mint/10 text-brand-green px-1.5 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    </div>
                  </div>
                  {r?.allocation && (r.allocation.meal_slot || r.allocation.portions_for_patient || r.allocation.nutritionist_note) && (
                    <div className="mt-2 pt-2 border-t border-brand-border/50 space-y-1" data-testid={`my-recipe-allocation-${r.id}`}>
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        {r.allocation.meal_slot_label && (
                          <span className="bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full font-medium">{r.allocation.meal_slot_label}</span>
                        )}
                        {r.allocation.portions_for_patient ? (
                          <span className="bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">
                            {r.allocation.portions_for_patient} {r.allocation.portions_for_patient === 1 ? 'dose' : 'doses'} a preparar
                          </span>
                        ) : null}
                        {r.allocation.computed_macros && (
                          <span className="text-brand-muted">
                            {Math.round(r.allocation.computed_macros.energia_kcal)} kcal · P {Math.round(r.allocation.computed_macros.proteina_g)}g
                          </span>
                        )}
                      </div>
                      {r.allocation.nutritionist_note && (
                        <p className="text-[11px] text-brand-text/80 italic">“{r.allocation.nutritionist_note}”</p>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-brand-text">{viewing.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {(viewing.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {(viewing.tags || []).map(t => <span key={t} className="text-[10px] bg-brand-mint/10 text-brand-green px-2 py-0.5 rounded-full">{t}</span>)}
                  </div>
                )}
                {(viewing.ingredients || []).length > 0 && (() => {
                  const portions = parseFloat(viewing.allocation?.portions_for_patient) || 0;
                  const baseServings = parseFloat(viewing.servings) || 1;
                  const factor = portions > 0 ? (portions / baseServings) : 1;
                  const showScaled = portions > 0 && portions !== baseServings;
                  const headerLabel = showScaled
                    ? `Ingredientes para as tuas doses (${portions})`
                    : (baseServings > 1 ? `Ingredientes (${baseServings} porções)` : 'Ingredientes');
                  const fmtQty = (q) => {
                    const num = parseFloat(q);
                    if (isNaN(num)) return q;
                    const scaled = num * factor;
                    return scaled >= 10 ? Math.round(scaled).toString() : (Math.round(scaled * 10) / 10).toString();
                  };
                  return (
                    <div data-testid="recipe-ingredients-block">
                      <p className="text-[11px] uppercase text-brand-muted mb-1">{headerLabel}</p>
                      <ul className="space-y-1">
                        {(viewing.ingredients || []).map((ing, i) => (
                          <li key={i} className="text-xs text-brand-text">
                            {typeof ing === 'string'
                              ? ing
                              : `${ing.quantity != null && ing.quantity !== '' ? fmtQty(ing.quantity) : ''} ${ing.unit || ''} ${ing.custom_name || ing.name || ''}`.trim()}
                          </li>
                        ))}
                      </ul>
                      {showScaled && (
                        <p className="text-[10px] text-brand-placeholder mt-1.5 italic" data-testid="recipe-scaled-note">
                          Quantidades ajustadas a partir da receita base ({baseServings} {baseServings === 1 ? 'porção' : 'porções'}).
                        </p>
                      )}
                    </div>
                  );
                })()}
                {viewing.instructions && (
                  <div>
                    <p className="text-[11px] uppercase text-brand-muted mb-1">Modo de preparação</p>
                    <p className="text-xs text-brand-text whitespace-pre-line">{viewing.instructions}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
